import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useEmployeeAuth } from '../hooks/useEmployeeAuth';
import AttendLogo from '../assets/attendlogo.png';
import AuthShell, { authAlertSx, authButtonSx, authTextFieldSx } from '../components/AuthShell';

const EmployeeLogin: React.FC = () => {
  const { t } = useTranslation('auth');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loginError, setLoginError] = useState<string | null>(null);
  const { login, loading, error, clearError, employee, mustChangePassword } = useEmployeeAuth();
  const navigate = useNavigate();

  const displayError = error || loginError;

  // Redirect if already logged in
  useEffect(() => {
    if (employee) {
      if (mustChangePassword) {
        navigate('/employee/change-password', { replace: true });
      } else {
        navigate('/employee/dashboard', { replace: true });
      }
    }
  }, [employee, mustChangePassword, navigate]);

  const validateUsername = (username: string): boolean => {
    if (!username) {
      setUsernameError(t('employeePortal.usernameRequired'));
      return false;
    }
    setUsernameError('');
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError(t('employeePortal.passwordRequired'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  const getSubmitErrorMessage = (err: any): string => {
    const data = err?.response?.data ?? err?.response;
    if (data?.message) return data.message;
    if (data?.errors) {
      const firstFieldErrors = Object.values(data.errors)[0];
      if (Array.isArray(firstFieldErrors) && firstFieldErrors.length > 0) {
        return String(firstFieldErrors[0]);
      }
    }
    if (err?.message) return err.message;
    return t('employeePortal.loginFailed');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLoginError(null);

    // Validate inputs
    const isUsernameValid = validateUsername(username);
    const isPasswordValid = validatePassword(password);

    if (!isUsernameValid || !isPasswordValid) {
      return;
    }

    try {
      await login({ username, password });
      // Redirect handled by useEffect
    } catch (err: any) {
      setLoginError(getSubmitErrorMessage(err));
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUsername(newValue);
    if (displayError && newValue !== username) {
      clearError();
      setLoginError(null);
    }
    if (usernameError) {
      validateUsername(newValue);
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPassword(newValue);
    if (displayError && newValue !== password) {
      clearError();
      setLoginError(null);
    }
    if (passwordError) {
      validatePassword(newValue);
    }
  };

  return (
    <AuthShell>
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
            px: 1.5,
            py: 1,
            borderRadius: 'var(--an-radius-md)',
            border: '1px solid var(--an-border-light)',
            backgroundColor: 'var(--an-surface)',
            boxShadow: 'var(--an-shadow-sm)',
          }}
        >
          <Box component="img" src={AttendLogo} alt="AttendNow" sx={{ height: 44, width: 'auto', display: 'block' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 'var(--an-radius-sm)',
              color: 'var(--an-primary)',
              backgroundColor: 'var(--an-primary-light)',
              flexShrink: 0,
            }}
          >
            <PersonIcon />
          </Box>
          <Typography variant="h4" component="h1" fontWeight={800} sx={{ color: 'var(--an-text-primary)' }}>
            {t('employeePortal.loginTitle')}
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: 'var(--an-text-secondary)', maxWidth: 440 }}>
          {t('employeePortal.loginSubtitle')}
        </Typography>
      </Box>

      {displayError && (
        <Alert severity="error" sx={authAlertSx}>
          {displayError}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label={t('employeePortal.usernameLabel')}
          value={username}
          onChange={handleUsernameChange}
          onBlur={() => validateUsername(username)}
          required
          margin="normal"
          error={!!usernameError}
          helperText={usernameError}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon />
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('employeePortal.passwordLabel')}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={handlePasswordChange}
          onBlur={() => validatePassword(password)}
          required
          margin="normal"
          error={!!passwordError}
          helperText={passwordError}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  edge="end"
                  aria-label="toggle password visibility"
                  sx={{ color: 'var(--an-text-secondary)' }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ ...authTextFieldSx, mb: 3 }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading || !!usernameError || !!passwordError}
          sx={authButtonSx}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : t('employeePortal.signIn')}
        </Button>
      </Box>
    </AuthShell>
  );
};

export default EmployeeLogin;
